import React, { FC, useCallback, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import styled from 'styled-components';
import { EmptyState } from '@cubeyou-team/hx-react-components';
import {
  SingleItemLoader,
  TaxonomyExplorerItemLoader,
} from '../ContentLoaders';
import SavedQuery from './components/SavedQuery/SavedQuery';

const StyledEmptyStateContainer = styled.div`
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const SavedQueries: FC<any> = ({
  items,
  onSavedQuerySelected,
  shouldCallLoadMore,
  isInitialLoading,
  isNextPageLoading,
  onLoadMoreRequest,
  emptyStateTitle,
  emptyStateSubtitle,
}) => {
  const scrollParentRef = useRef<any | null>(null);
  const loadMoreItems = useCallback(() => {
    if (isNextPageLoading || isInitialLoading) {
      return false;
    }
    onLoadMoreRequest && onLoadMoreRequest();
  }, [isNextPageLoading, isInitialLoading, onLoadMoreRequest]);
  const isItemLoaded = (index) => !shouldCallLoadMore() || index < items.length;

  if (isInitialLoading) {
    return <TaxonomyExplorerItemLoader />;
  }

  const listItem = ({ item, index }) => {
    if (!isItemLoaded(index)) {
      return (
        <div key={`loader-${item.id}-${index}`}>
          <SingleItemLoader />
        </div>
      );
    }
    return (
      <SavedQuery
        tabIndex={index + 1}
        key={item.id}
        item={item}
        onSavedQuerySelected={onSavedQuerySelected}
      />
    );
  };

  if (!items || items.length === 0) {
    return (
      <StyledEmptyStateContainer>
        <EmptyState
          icon="search"
          mainText={emptyStateTitle}
          smallText={emptyStateSubtitle}
          size="small"
        />
      </StyledEmptyStateContainer>
    );
  }

  return (
    <div
      style={{ height: '100%', overflow: 'auto', paddingBottom: '60px' }}
      ref={(ref) => {
        scrollParentRef.current = ref;
      }}
    >
      <InfiniteScroll
        pageStart={0}
        loadMore={loadMoreItems}
        hasMore={shouldCallLoadMore()}
        threshold={5}
        loader={
          <div key="loader">
            <SingleItemLoader />
          </div>
        }
        useWindow={false}
        getScrollParent={() => scrollParentRef.current}
      >
        {items.map((item, index) => (
          <div key={`list-item-${item.id}-${index}`}>
            {listItem({ item, index })}
          </div>
        ))}
      </InfiniteScroll>
    </div>
  );
};

export default SavedQueries;
